import React, { useEffect, useState } from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../../core/ToxnotReactModal';
import TextAreaInput from '../../core/TextAreaInput';
import { DropdownMenu } from '../../core/DropdownMenu';
import fdaCitationData from '../../../utilities/FDACitation/Data';
import { FDAHelper } from './FDAHelper';
import DataTable from '../../core/DataTable';
import MultiDropdown from '../../core/MultipleDropdown/MultipleDropdown';
import SearchableDropdown from '../../core/SearchableDropdown/SearchableDropdown';
import TextInput from '../../core/TextInput';

import "./styles.css";

interface FDACitationsDataTableProps {
    columns: string[],
    rows?: string[][];
    onResponseChange: (value: any) => void;
    localizer: IStringLocalizer;
}

const FDACitationsDataTable: React.FC<FDACitationsDataTableProps> = ({ columns, rows, onResponseChange, localizer }) => {
    const [currentRows, setCurrentRows] = useState(rows || []);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [comment, setComment] = useState("");
    const [description, setDescription] = useState("");
    const [anyRestrictions, setAnyRestrictions] = useState("");
    const [part, setPart] = useState("");
    const [section, setSection] = useState("");
    const [citation, setCitation] = useState("");
    const [displayErrorMessageForPart, setDisplayErrorMessageForPart] = useState(false);
    const [displayErrorMessageForSection, setDisplayErrorMessageForSection] = useState(false);
    const [selectedSubSections, setSelectedSubSections] = useState([] as string[]);
    const [selectableSubSections, setSelectableSubSections] = useState([] as string[]);

    useEffect(() => {
        const displayFormatRows = rows ? [...rows] : [];

        displayFormatRows.forEach(
            r => r[FDAHelper.partAndSectionIndex] = r[FDAHelper.partAndSectionIndex].startsWith(FDAHelper.citationPrefix)
                ? r[FDAHelper.partAndSectionIndex]
                : `${FDAHelper.citationPrefix}${r[FDAHelper.partAndSectionIndex]}`
        );
        setCurrentRows(displayFormatRows);
    }, [rows]);

    const onAddOrEdit = (rowIndex: number) => {
        setCurrentIndex(rowIndex);
        parseCurrentRow(rowIndex);

        $('#' + FDAHelper.addEditFDACitationModalId).modal('show');
    }

    const parseCurrentRow = (rowIndex: number) => {
        const row = currentRows ? currentRows[rowIndex ?? currentIndex] : null;

        if (row) {
            setDescription(row[FDAHelper.descriptionIndex]);
            setAnyRestrictions(row[FDAHelper.anyRestrictionsIndex]);
            setComment(row[FDAHelper.commentsIndex]);
            setCitation(row[FDAHelper.partAndSectionIndex].replace(FDAHelper.citationPrefix, '') + FDAHelper.citationDescriptionSeparator + row[FDAHelper.descriptionIndex]);

            const partAndSection = row[FDAHelper.partAndSectionIndex];

            if (partAndSection !== '') {
                parseAndSetPartSectionAndSubsections(partAndSection, row[FDAHelper.applicableSubsectionsIndex]);
            }
        }
    }

    const clearCurrentCitationData = () => {
        setAnyRestrictions("");
        setComment("");
        setPart("");
        setSection("");
        clearDescriptionAndCitation();
        setSelectedSubSections([]);
        setSelectableSubSections([]);
        setDisplayErrorMessageForPart(false);
        setDisplayErrorMessageForSection(false);
    }

    const onRowUpdate = () => {
        let hasErrors = false;

        if (partNotSelected()) {
            setDisplayErrorMessageForPart(true);
            hasErrors = true;
        }

        if (sectionNotSelected()) {
            setDisplayErrorMessageForSection(true);
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        const updatedMatrix = [...currentRows];

        if (currentIndex >= updatedMatrix.length) {
            let newRow = Array(columns.length).fill('');

            updatedMatrix.push(newRow);
        }

        updatedMatrix[currentIndex][FDAHelper.partAndSectionIndex] = getConatenatedPartAndSection(part, section);
        updatedMatrix[currentIndex][FDAHelper.descriptionIndex] = description;
        updatedMatrix[currentIndex][FDAHelper.applicableSubsectionsIndex] = selectedSubSections.join(FDAHelper.subSectionsSeparator);
        updatedMatrix[currentIndex][FDAHelper.anyRestrictionsIndex] = anyRestrictions;
        updatedMatrix[currentIndex][FDAHelper.commentsIndex] = comment;

        onChange(updatedMatrix);
        clearCurrentCitationData();

        $('#' + FDAHelper.addEditFDACitationModalId).modal('hide');
    }

    const partNotSelected = () => {
        return part.trim().length === 0 || part === FDAHelper.dropDownDefaultValue;
    }

    const sectionNotSelected = () => {
        return section.trim().length === 0 || section === FDAHelper.dropDownDefaultValue;
    }

    const onChange = (updatedMatrix: any) => {
        if (onResponseChange) {
            let updatedRows = updatedMatrix ? [...updatedMatrix] : [];

            updatedMatrix.forEach((r: string[]) => r[FDAHelper.partAndSectionIndex] = r[FDAHelper.partAndSectionIndex].replace(FDAHelper.citationPrefix, ''));
            onResponseChange(updatedRows);
        }

        setCurrentRows(updatedMatrix);
    }

    const getCitationDescription = (partValue: string, sectionValue: string) => {
        return fdaCitationData.firstOrDefault(f => f.partNumber.toString() === partValue)?.sections.firstOrDefault(s => s.sectionName.toString() === sectionValue)?.description;
    }

    const onPartChange = (value: string) => {
        if (part !== value) {
            setPart(value);
            setSection("");
            setSelectedSubSections([]);
            clearDescriptionAndCitation();
        }
    }

    const onSectionChange = (value: string) => {
        if (section !== value) {
            setSection(value);

            if (value !== FDAHelper.dropDownDefaultValue) {
                let newDescription = getCitationDescription(part, value);

                setCitation(getCitation(part, value, newDescription === undefined ? "" : newDescription));
                setDescription(newDescription ?? "");
                setSelectedSubSections([]);
                setSelectableSubSections(getSubsections(part, value));
            } else {
                clearDescriptionAndCitation();
            }
        }
    }

    const getConatenatedPartAndSection = (partValue: string, sectionValue: string) => {
        return sectionValue !== FDAHelper.notApplicableSectionsValue ? partValue + FDAHelper.partAndCitationSeparator + sectionValue : partValue;
    }

    const getCitation = (partValue: string, sectionValue: string, descriptionValue: string) => {
        return getConatenatedPartAndSection(partValue, sectionValue) + FDAHelper.citationDescriptionSeparator + descriptionValue;
    }

    const clearDescriptionAndCitation = () => {
        setDescription("");
        setCitation("");
    }

    const clearCitation = () => {
        setPart("");
        setSection("");
        setSelectedSubSections([]);
        clearDescriptionAndCitation();
    }

    const getSubsections = (partValue: string, sectionValue: string) => {
        const subSectionsList = fdaCitationData.firstOrDefault(f => f.partNumber.toString() === partValue)?.sections.firstOrDefault(s => s.sectionName.toString() === sectionValue)?.subSections;

        return subSectionsList === undefined ? [] : subSectionsList.map(x => x.subSectionName);
    }

    const onSearchCitationSelected = (citation: string) => {
        const partSectionAndDescription = citation.split(FDAHelper.citationDescriptionSeparator);

        setDescription(partSectionAndDescription[1]);
        parseAndSetPartSectionAndSubsections(partSectionAndDescription[0], '');
        setCitation(citation);
    }

    const getCitations = () => {
        return fdaCitationData.selectMany(f => f.sections.map(s => getCitation(f.partNumber.toString(), s.sectionName, s.description)));
    }

    const parseAndSetPartSectionAndSubsections = (concatenatedPartAndSection: string, concatenatedSubsections: string) => {
        const partAndSection = concatenatedPartAndSection.split(FDAHelper.partAndCitationSeparator);
        const partValue = partAndSection[FDAHelper.partIndex].replace(FDAHelper.citationPrefix, '');
        const sectionValue = partAndSection[FDAHelper.sectionIndex] ?? FDAHelper.notApplicableSectionsValue;

        if (!fdaCitationData.some(f => f.partNumber.toString() === partValue) || !fdaCitationData.firstOrDefault(f => f.partNumber.toString() === partValue)?.sections.some(s => s.sectionName === sectionValue)) {
            clearCitation();
        } else {
            const subsections = concatenatedSubsections.split(FDAHelper.subSectionsSeparator);

            setSelectedSubSections(subsections.some(s => !getSubsections(partValue, sectionValue).some(ss => ss === s)) ? [] : subsections);
        }

        setPart(partValue);
        setSection(sectionValue);
        setSelectableSubSections(getSubsections(partValue, sectionValue));
    }

    const requiredColumns = columns.map((c, index) => {
        if (index === 0) {
            return true;
        }

        return false;
    });

    return (
        <>
            <DataTable
                localizer={localizer}
                columns={columns}
                rows={currentRows}
                onResponseChange={onChange}
                radioColumns={[]}
                inlineEditor={false}
                addButtonText={localizer.localizeString("Add New Row")}
                customAdd={() => onAddOrEdit(currentRows.length)}
                customEdit={onAddOrEdit}
                dragDropEnabled={false}
                requiredColumns={requiredColumns}
            />

            <ToxnotReactModal modalId={FDAHelper.addEditFDACitationModalId} localizedTitle={localizer.localizeString("Add/Edit FDA Citation")} onClose={clearCurrentCitationData}>
                <ToxnotReactModalBody>
                    <div>
                        <div className="row-container">
                            <label className="w-50">Part <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span></label>
                            <label className="w-50 m-l-10">Section <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span></label>
                        </div>

                        <div className="row-container">
                            <div className="w-50 m-r-5">
                                <DropdownMenu key={`${currentIndex}-${FDAHelper.partIndex}`}
                                    options={[FDAHelper.dropDownDefaultValue].concat(fdaCitationData.map(f => f.partNumber.toString()))}
                                    onOptionSelected={onPartChange}
                                    getFriendlyNameForOptions={a => a}
                                    value={part}
                                    getUniqueValueForOption={o => o} />
                                {displayErrorMessageForPart && partNotSelected() &&
                                    <p className="p-t-5 text-danger">Part is required.</p>
                                }
                            </div>
                            <div className="w-50">
                                <DropdownMenu key={`${currentIndex}-${FDAHelper.sectionIndex}`}
                                    options={[FDAHelper.dropDownDefaultValue].concat(fdaCitationData.firstOrDefault(f => f.partNumber.toString() === part)?.sections.map(s => s.sectionName) ?? [])}
                                    onOptionSelected={onSectionChange}
                                    getFriendlyNameForOptions={a => a}
                                    value={section}
                                    getUniqueValueForOption={o => o} />
                                {displayErrorMessageForSection && sectionNotSelected() &&
                                    <p className="p-t-5 text-danger">Section is required.</p>
                                }
                            </div>
                        </div>

                        <label className="m-t-10">{localizer.localizeString("Search Citation")}</label>
                        <SearchableDropdown options={getCitations()}
                            onSelect={onSearchCitationSelected}
                            onSearchTermChanged={() => { }}
                            getFriendlyNameForOptions={o => o}
                            clearSearchTermAfterSelect={true}>
                        </SearchableDropdown>

                        <label className="m-t-10">{localizer.localizeString("Citation")}</label>
                        <TextInput key={`${currentIndex}-${FDAHelper.descriptionIndex}`}
                            disabled={true}
                            onValueChange={() => { }}
                            value={citation}>
                        </TextInput>
                        
                        <label className="m-t-10">{localizer.localizeString("Applicable Subsections")}</label>
                        <MultiDropdown key={`${currentIndex}-applicable-subsections`}
                            options={selectableSubSections}
                            selectedOptions={selectedSubSections}
                            onChange={setSelectedSubSections}
                            disabled={selectableSubSections.length === 0}>
                        </MultiDropdown>

                        <label className="m-t-10">{localizer.localizeString("Any Restrictions")}</label>
                        <TextAreaInput key={`${currentIndex}-${FDAHelper.anyRestrictionsIndex}`}
                            value={anyRestrictions}
                            onValueChange={setAnyRestrictions}>
                        </TextAreaInput>

                        <label className="m-t-10">{localizer.localizeString("Comments")}</label>
                        <TextAreaInput key={`${currentIndex}-${FDAHelper.commentsIndex}`}
                            value={comment}
                            onValueChange={setComment}>
                        </TextAreaInput>
                    </div>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter onCancel={clearCurrentCitationData} localizer={localizer} >
                    <button key={currentIndex} type="button" className="btn btn-toxnot-primary" onClick={onRowUpdate}>{localizer.localizeString("Update")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </>
    )
}

export default FDACitationsDataTable;